import type { PageHelp } from 'help/types';

export const clientBalanceHelp: PageHelp = {
  title: 'Client balance',
  intro:
    'Every client balance (saldo) per GrECo company and currency: money received from a client that is not yet matched to any premium, mostly the overpaid part of a cleared bank statement entry. From here you clear that balance against the client\'s outstanding premium, which creates payments, or look at how the balance built up and was used.',
  sections: [
    'Getting around',
    'Finding a client balance',
    'Reading the list',
    'Clearance: choosing the entries',
    'Clearance: amounts and saving',
    'Balance history',
  ],
  contexts: {
    'clear-dialog': 'Click the "Clear client balance" button (split-arrow icon) at the end of a client row.',
    'info-dialog': 'Click the "Client balance information" button (i icon) at the end of a client row.',
    'info-payments':
      'In "Client balance clearance information", click the i button at the end of a balance entry that has one.',
  },
  tips: [
    'A client has one row per GrECo company and currency. A positive balance is money that can still be used for premium; clearing it here creates real payments, which then show up on the Payments page for the declaration to the insurer.',
    'A dialog title ending in "(discounts)" means the balance belongs to GrECo itself: the clearance then pays discount debt instead of premium debt, starts with no client selected and offers the "Broker premium collection" filter.',
    'The list does not refresh by itself. After clearing bank statements, or after a colleague worked on the same client, click the refresh button.',
    'You only see balances of the GrECo companies ticked in the company menu, and only clients you have permission for. Clients outside your permission groups are left out without any message.',
  ],
  steps: [
    {
      selector: '[data-help="client-balance-page-switcher"]',
      title: 'Switch to another PCT page',
      body: 'The icons jump between the PCT pages: bank statements, insurer reports, client balance, payment plans, reminders and payments. The highlighted icon is the page you are on.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Pereiti į kitą PCT puslapį',
          body: 'Piktogramomis pereinama tarp PCT puslapių: banko išrašų, draudiko ataskaitų, kliento balanso, mokėjimų plano, priminimų ir mokėjimų. Paryškinta piktograma rodo puslapį, kuriame esate.',
        },
        lv: {
          title: 'Pāriet uz citu PCT lapu',
          body: 'Ar ikonām var pārslēgties starp PCT lapām: bankas konta izraksti, apdrošinātāja atskaites, klienta bilance, maksājumu plāni, atgādinājumi un maksājumi. Izceltā ikona rāda lapu, kurā atrodaties.',
        },
      },
    },
    {
      selector: '[data-help="app-user-manual"]',
      title: 'Download the user manual',
      body: 'Downloads the complete PCT user manual as a PDF, with screenshots of every page. You get it in the language selected in Settings: Lithuanian or Latvian when the app is in that language, English otherwise. The page guide offers the same download (the book next to EN / LT / LV) in the guide’s language.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Atsisiųsti naudotojo vadovą',
          body: 'Atsisiunčia visą PCT naudotojo vadovą PDF formatu su visų puslapių ekrano kopijomis. Gaunate jį kalba, pasirinkta „Nustatymuose“: lietuvių arba latvių, kai programa veikia ta kalba, kitu atveju – anglų. Puslapio vadove tas pats atsisiuntimas (knygos piktograma šalia EN / LT / LV) pateikiamas vadovo kalba.',
        },
        lv: {
          title: 'Lejupielādēt lietotāja rokasgrāmatu',
          body: 'Lejupielādē pilnu PCT lietotāja rokasgrāmatu PDF formātā ar visu lapu ekrānuzņēmumiem. Jūs to saņemat valodā, kas izvēlēta sadaļā “Settings”: latviešu vai lietuviešu, ja lietotne darbojas šajā valodā, citādi – angļu. Lapas ceļvedī tā pati lejupielāde (grāmatas ikona blakus EN / LT / LV) ir pieejama ceļveža valodā.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-refresh"]',
      title: 'Reload the balances',
      body: 'Fetches the client balances again from the server for the ticked companies. The list never updates by itself, so use it after clearing bank statements or after a colleague worked on a client. The button is disabled while the list is loading.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Atnaujinti balansus',
          body: 'Iš naujo įkelia pažymėtų įmonių klientų balansus iš serverio. Sąrašas pats neatsinaujina, todėl spauskite po banko išrašų užskaitymo arba kai kolega dirbo su klientu. Kol sąrašas kraunamas, mygtukas neaktyvus.',
        },
        lv: {
          title: 'Pārlādēt bilances',
          body: 'No servera atkārtoti ielādē atzīmēto uzņēmumu klientu bilances. Saraksts pats neatjaunojas, tāpēc lietojiet to pēc bankas izrakstu ieskaitīšanas vai kad kolēģis strādājis ar klientu. Kamēr saraksts ielādējas, poga nav aktīva.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-companies"]',
      title: 'Choose the GrECo companies',
      body: 'Opens the list of GrECo companies you have access to. Tick the companies whose client balances you want to see; at least one must stay ticked. The balances reload when you close the menu.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Pasirinkti GrECo įmones',
          body: 'Atveria jums prieinamų GrECo įmonių sąrašą. Pažymėkite įmones, kurių klientų balansus norite matyti; bent viena turi likti pažymėta. Uždarius meniu balansai įkeliami iš naujo.',
        },
        lv: {
          title: 'Izvēlēties GrECo uzņēmumus',
          body: 'Atver jums pieejamo GrECo uzņēmumu sarakstu. Atzīmējiet uzņēmumus, kuru klientu bilances vēlaties redzēt; vismaz vienam jāpaliek atzīmētam. Aizverot izvēlni, bilances tiek ielādētas no jauna.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-search"]',
      title: 'Search the balances',
      body: 'Filters the list shortly after you stop typing: client name or number, company, currency, balance amount or last modified date (dd.mm.yyyy). It only searches the rows already loaded and does not ask the server again.',
      section: 'Finding a client balance',
      i18n: {
        lt: {
          title: 'Ieškoti balansų',
          body: 'Filtruoja sąrašą netrukus po to, kai baigiate rašyti: pagal kliento pavadinimą ar numerį, įmonę, valiutą, balanso sumą arba paskutinio koregavimo datą (dd.mm.yyyy). Ieškoma tik jau įkeltose eilutėse, serveris iš naujo neužklausiamas.',
        },
        lv: {
          title: 'Meklēt bilances',
          body: 'Filtrē sarakstu īsi pēc tam, kad beidzat rakstīt: pēc klienta nosaukuma vai numura, uzņēmuma, valūtas, bilances summas vai pēdējās izmaiņu datuma (dd.mm.yyyy). Meklē tikai jau ielādētajās rindās, serveris netiek vaicāts no jauna.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-export-excel"]',
      title: 'Export the client balances to Excel',
      body: '"Export Excel" downloads the balances the list shows right now — client, company, currency, balance and last modified date — so the search text and the zero-balance switch apply to the file too. The button is disabled while the list is loading.',
      section: 'Finding a client balance',
      i18n: {
        lt: {
          title: 'Eksportuoti klientų balansus į Excel',
          body: '„Export Excel“ atsisiunčia balansus, kurie šiuo metu rodomi sąraše — klientą, įmonę, valiutą, balansą ir paskutinio koregavimo datą, todėl paieškos tekstas ir nulinių balansų jungiklis taikomi ir failui. Kol sąrašas įkeliamas, mygtukas neaktyvus.',
        },
        lv: {
          title: 'Eksportēt klientu bilances uz Excel',
          body: '“Export Excel” lejupielādē bilances, kas pašlaik redzamas sarakstā — klientu, uzņēmumu, valūtu, bilanci un pēdējo izmaiņu datumu, tāpēc meklēšanas teksts un nulles bilanču slēdzis attiecas arī uz failu. Kamēr saraksts ielādējas, poga nav aktīva.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-zero-toggle"]',
      title: 'Show clients with a zero balance',
      body: 'Rows whose balance is 0 (to two decimals) are hidden by default. Tick "Show zero balance clients" to list them too, for example to check a client whose money has been used up. Changing it also clears any row selection.',
      section: 'Finding a client balance',
      i18n: {
        lt: {
          title: 'Rodyti klientus su nuliniu balansu',
          body: 'Eilutės, kurių balansas lygus 0 (dviejų skaičių po kablelio tikslumu), pagal nutylėjimą paslėptos. Pažymėkite "Rodyti nulinius balansus", kad jos taip pat būtų rodomos, pvz., norėdami patikrinti klientą, kurio pinigai jau panaudoti. Pakeitus nustatymą, pažymėtos eilutės atžymimos.',
        },
        lv: {
          title: 'Rādīt klientus ar nulles bilanci',
          body: 'Rindas, kuru bilance ir 0 (ar divu zīmju precizitāti aiz komata), pēc noklusējuma ir paslēptas. Atzīmējiet "Show zero balance clients", lai tās arī parādītu, piemēram, lai pārbaudītu klientu, kura nauda jau izlietota. Mainot iestatījumu, rindu atlase tiek noņemta.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-results-count"]',
      title: 'Check how many rows are shown',
      body: 'The number of balance rows that match the search and the zero-balance setting. A client has one row per company and currency, so one client can account for several rows.',
      section: 'Finding a client balance',
      i18n: {
        lt: {
          title: 'Patikrinti, kiek eilučių rodoma',
          body: 'Balanso eilučių, atitinkančių paiešką ir nulinių balansų nustatymą, skaičius. Klientas turi po vieną eilutę kiekvienai įmonei ir valiutai, todėl vienam klientui gali priklausyti kelios eilutės.',
        },
        lv: {
          title: 'Pārbaudīt, cik rindu ir redzamas',
          body: 'Bilances rindu skaits, kas atbilst meklēšanai un nulles bilanču iestatījumam. Klientam ir viena rinda katram uzņēmumam un valūtai, tāpēc vienam klientam var būt vairākas rindas.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-grid"]',
      title: 'Read the client balance list',
      body: 'Each row is one client\'s balance in one GrECo company and currency. "Balance" is the unmatched money; "Last modified date" is the latest change to any of the client\'s balance entries, not only this row\'s. Click a column header to sort, and click the client name to open the client in GOS.',
      section: 'Reading the list',
      i18n: {
        lt: {
          title: 'Skaityti klientų balansų sąrašą',
          body: 'Kiekviena eilutė yra vieno kliento balansas vienoje GrECo įmonėje ir viena valiuta. "Balansas" yra dar nesusieti pinigai; "Paskutinį kartą koreguota" rodo naujausią bet kurio kliento balanso įrašo pakeitimą, ne tik šios eilutės. Spustelėkite stulpelio antraštę, kad surūšiuotumėte, o kliento pavadinimą, kad atvertumėte klientą GOS sistemoje.',
        },
        lv: {
          title: 'Lasīt klientu bilanču sarakstu',
          body: 'Katra rinda ir viena klienta bilance vienā GrECo uzņēmumā un vienā valūtā. "Balance" ir vēl nesasaistītā nauda; "Last modified date" ir jaunākās izmaiņas jebkurā klienta bilances ierakstā, ne tikai šajā rindā. Noklikšķiniet uz kolonnas virsraksta, lai kārtotu, un uz klienta nosaukuma, lai atvērtu klientu GOS.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-row-info"]',
      title: 'Open the balance history',
      body: 'The i button at the end of a row opens "Client balance clearance information" with every entry that changed this balance and the running balance after each one. Nothing can be changed there; use it to see where the money came from and how it was used.',
      section: 'Reading the list',
      optional: true,
      i18n: {
        lt: {
          title: 'Atverti balanso istoriją',
          body: 'Mygtukas i eilutės gale atveria "Client balance clearance information" su visais įrašais, keitusiais šį balansą, ir balansu po kiekvieno jų. Ten nieko pakeisti negalima; naudokite, kad pamatytumėte, iš kur atėjo pinigai ir kaip jie panaudoti.',
        },
        lv: {
          title: 'Atvērt bilances vēsturi',
          body: 'Poga i rindas beigās atver "Client balance clearance information" ar visiem ierakstiem, kas mainījuši šo bilanci, un bilanci pēc katra no tiem. Tur neko nevar mainīt; izmantojiet to, lai redzētu, no kurienes nauda nāca un kā tā izlietota.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-row-clear"]',
      title: 'Clear the balance against premium',
      body: 'The split-arrow button opens "Clear client balance" for this client, company and currency. For a normal client the outstanding premium entries load straight away with suggested amounts; for GrECo\'s own balance the dialog opens empty and works on discounts.',
      section: 'Reading the list',
      optional: true,
      i18n: {
        lt: {
          title: 'Užskaityti balansą įmokoms',
          body: 'Mygtukas su išsišakojusia rodykle atveria "Užskaityti kliento balansą" šiam klientui, įmonei ir valiutai. Įprastam klientui neapmokėtos įmokos įkeliamos iš karto su siūlomomis sumomis; paties GrECo balansui langas atsidaro tuščias ir dirbama su nuolaidomis.',
        },
        lv: {
          title: 'Ieskaitīt bilanci prēmijās',
          body: 'Poga ar sazarotu bultu atver "Clear client balance" šim klientam, uzņēmumam un valūtai. Parastam klientam neapmaksātās prēmijas tiek ielādētas uzreiz ar ieteiktajām summām; paša GrECo bilancei logs atveras tukšs un strādā ar atlaidēm.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-dialog"]',
      title: 'Work in the clearance dialog',
      body: 'The title shows the client and "(discounts)" when the balance belongs to GrECo itself. On the left you narrow down the outstanding premium entries, on the right you decide how much of the balance goes to each one, and "Clear" saves the payments.',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Dirbti užskaitymo lange',
          body: 'Pavadinime rodomas klientas ir "(discounts)", kai balansas priklauso pačiam GrECo. Kairėje susiaurinate neapmokėtų įmokų sąrašą, dešinėje nusprendžiate, kiek balanso skirti kiekvienai įmokai, o "Išvalyti" išsaugo mokėjimus.',
        },
        lv: {
          title: 'Strādāt ieskaitīšanas logā',
          body: 'Virsrakstā redzams klients un "(discounts)", ja bilance pieder pašam GrECo. Kreisajā pusē sašaurina neapmaksāto prēmiju sarakstu, labajā pusē nosaka, cik daudz bilances novirzīt katrai prēmijai, un "Clear" saglabā maksājumus.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-dates"]',
      title: 'Limit the entries by booking date',
      body: 'From and to booking date of the premium entries offered. It starts about three months back and ends 15 days ahead; type d.m.yyyy or pick a date, and picking the same date again empties the field. Widen the range if an older entry is missing, then click Search.',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Apriboti įmokas pagal užregistravimo datą',
          body: 'Siūlomų įmokų užregistravimo data nuo ir iki. Pradžioje nustatoma maždaug trys mėnesiai atgal ir 15 dienų į priekį; įveskite d.m.yyyy arba pasirinkite datą, o pasirinkus tą pačią datą dar kartą laukas išvalomas. Jei trūksta senesnės įmokos, išplėskite laikotarpį ir spauskite "Ieškoti".',
        },
        lv: {
          title: 'Ierobežot ierakstus pēc grāmatošanas datuma',
          body: 'Piedāvāto prēmiju grāmatošanas datums no un līdz. Sākumā tas ir aptuveni trīs mēneši atpakaļ un 15 dienas uz priekšu; ievadiet d.m.yyyy vai izvēlieties datumu, un, vēlreiz izvēloties to pašu datumu, lauks tiek notīrīts. Ja trūkst vecāka ieraksta, paplašiniet periodu un spiediet "Search".',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-clients"]',
      title: 'Choose whose premium is paid',
      body: 'Starts with the balance owner for a normal client and empty for GrECo\'s own balance. Add more clients if this money should also pay their premium, for example a related company. At least one client is needed: until then "Please select a client" shows and Search stays disabled.',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti, kieno įmokos apmokamos',
          body: 'Įprastam klientui iš pradžių pasirenkamas balanso savininkas, GrECo balansui laukas tuščias. Pridėkite daugiau klientų, jei šiais pinigais reikia apmokėti ir jų įmokas, pvz., susijusios įmonės. Reikia bent vieno kliento: kol jo nėra, rodoma "Please select a client", o "Ieškoti" neaktyvus.',
        },
        lv: {
          title: 'Izvēlēties, kura prēmijas apmaksāt',
          body: 'Parastam klientam sākumā izvēlēts bilances īpašnieks, GrECo bilancei lauks ir tukšs. Pievienojiet vairāk klientu, ja ar šo naudu jāapmaksā arī viņu prēmijas, piemēram, saistīta uzņēmuma. Nepieciešams vismaz viens klients: līdz tam redzams "Please select a client" un "Search" nav aktīva.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-insurers"]',
      title: 'Filter by insurer',
      body: 'First pick the insurer country, which loads that country\'s insurers, then pick one or more insurers. Only premium entries of those insurers are offered; leave it empty for all insurers.',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Filtruoti pagal draudiką',
          body: 'Pirmiausia pasirinkite draudiko šalį, tada įkeliami tos šalies draudikai, ir pasirinkite vieną ar kelis draudikus. Siūlomos tik tų draudikų įmokos; palikite tuščią, jei reikia visų draudikų.',
        },
        lv: {
          title: 'Filtrēt pēc apdrošinātāja',
          body: 'Vispirms izvēlieties apdrošinātāja valsti, tad tiek ielādēti šīs valsts apdrošinātāji, un izvēlieties vienu vai vairākus apdrošinātājus. Tiek piedāvātas tikai šo apdrošinātāju prēmijas; atstājiet tukšu, ja vajag visus.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-payers"]',
      title: 'Filter by payer',
      body: 'Limits the entries to premium owed by the selected payers. Leave it empty to include every payer.',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Filtruoti pagal mokėtoją',
          body: 'Rodo tik pasirinktų mokėtojų mokėtinas įmokas. Palikite tuščią, jei reikia visų mokėtojų.',
        },
        lv: {
          title: 'Filtrēt pēc maksātāja',
          body: 'Rāda tikai izvēlēto maksātāju maksājamās prēmijas. Atstājiet tukšu, lai iekļautu visus maksātājus.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-reference-numbers"]',
      title: 'Filter by payment reference number',
      body: 'Enter one or more payment reference numbers, which are reminder numbers. Only premium entries that were sent in one of those reminders are offered.',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Filtruoti pagal mokėjimo numerį',
          body: 'Įveskite vieną ar kelis mokėjimo numerius, t. y. priminimų numerius. Siūlomos tik tos įmokos, kurios buvo išsiųstos viename iš šių priminimų.',
        },
        lv: {
          title: 'Filtrēt pēc maksājuma atsauces numura',
          body: 'Ievadiet vienu vai vairākus maksājuma atsauces numurus, t. i., atgādinājumu numurus. Tiek piedāvātas tikai prēmijas, kas bija iekļautas kādā no šiem atgādinājumiem.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-currency"]',
      title: 'Check the currency',
      body: 'Set to the currency of the balance row and offers only that one, because a balance can only pay premium in its own currency.',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Patikrinti valiutą',
          body: 'Nustatyta balanso eilutės valiuta ir siūloma tik ji, nes balansu galima apmokėti tik tos pačios valiutos įmokas.',
        },
        lv: {
          title: 'Pārbaudīt valūtu',
          body: 'Iestatīta bilances rindas valūta, un tiek piedāvāta tikai tā, jo ar bilanci var apmaksāt tikai tās pašas valūtas prēmijas.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-service-segments"]',
      title: 'Filter by service segment',
      body: 'Limits the entries to insurance lines that belong to the selected service segments. Leave it empty for all lines.',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Filtruoti pagal paslaugų segmentą',
          body: 'Rodo tik įmokas tų draudimo rūšių, kurios priklauso pasirinktiems paslaugų segmentams. Palikite tuščią, jei reikia visų rūšių.',
        },
        lv: {
          title: 'Filtrēt pēc pakalpojumu segmenta',
          body: 'Rāda tikai to apdrošināšanas veidu prēmijas, kas pieder izvēlētajiem pakalpojumu segmentiem. Atstājiet tukšu, lai iekļautu visus veidus.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-policies"]',
      title: 'Filter by policy number',
      body: 'Enter one or more policy numbers to offer only premium entries of those policies. Useful when the client said which policy the money was meant for.',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Filtruoti pagal poliso numerį',
          body: 'Įveskite vieną ar kelis poliso numerius, kad būtų siūlomos tik tų polisų įmokos. Naudinga, kai klientas nurodė, kuriam polisui skirti pinigai.',
        },
        lv: {
          title: 'Filtrēt pēc polises numura',
          body: 'Ievadiet vienu vai vairākus polišu numurus, lai piedāvātu tikai šo polišu prēmijas. Noderīgi, ja klients norādījis, kurai polisei nauda paredzēta.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-subpolicies"]',
      title: 'Filter by policy subnumber',
      body: 'Enter one or more policy subnumbers to narrow the entries further, for example to one insured object or one annex of a policy.',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Filtruoti pagal poliso subnumerį',
          body: 'Įveskite vieną ar kelis poliso subnumerius, kad dar labiau susiaurintumėte įmokų sąrašą, pvz., iki vieno apdrausto objekto ar poliso priedo.',
        },
        lv: {
          title: 'Filtrēt pēc polises apakšnumura',
          body: 'Ievadiet vienu vai vairākus polises apakšnumurus, lai vēl vairāk sašaurinātu sarakstu, piemēram, līdz vienam apdrošinātajam objektam vai polises pielikumam.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-broker-collection"]',
      title: 'Filter by broker premium collection',
      body: 'Shown only for GrECo\'s own (discount) balance. "Yes" offers entries whose premium GrECo collects, "No" all other entries and "Select all" both; it starts at "Yes".',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Filtruoti pagal brokerio įmokų surinkimą',
          body: 'Rodoma tik paties GrECo (nuolaidų) balansui. "Yes" siūlo įmokas, kurias renka GrECo, "No" visas kitas, o "Pasirinkti viską" abi grupes; pradžioje pasirinkta "Yes".',
        },
        lv: {
          title: 'Filtrēt pēc brokera prēmiju iekasēšanas',
          body: 'Redzams tikai paša GrECo (atlaižu) bilancei. "Yes" piedāvā prēmijas, kuras iekasē GrECo, "No" visas pārējās un "Select all" abas grupas; sākumā izvēlēts "Yes".',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-search-button"]',
      title: 'Load the outstanding entries',
      body: 'Loads the unpaid premium entries that match the filters (up to 5000) and pre-fills "To be cleared": the balance is spread over the entries in list order until it runs out. Amounts you typed before are replaced. Disabled while no client is selected.',
      section: 'Clearance: choosing the entries',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Įkelti neapmokėtas įmokas',
          body: 'Įkelia filtrus atitinkančias neapmokėtas įmokas (iki 5000) ir iš anksto užpildo "To be cleared": balansas paskirstomas įmokoms sąrašo tvarka, kol baigiasi. Anksčiau įvestos sumos pakeičiamos. Neaktyvus, kol nepasirinktas klientas.',
        },
        lv: {
          title: 'Ielādēt neapmaksātās prēmijas',
          body: 'Ielādē filtriem atbilstošās neapmaksātās prēmijas (līdz 5000) un iepriekš aizpilda "To be cleared": bilance tiek sadalīta prēmijām saraksta secībā, līdz tā beidzas. Iepriekš ievadītās summas tiek aizstātas. Nav aktīva, kamēr nav izvēlēts klients.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-payment-date"]',
      title: 'Set the payment date',
      body: 'The date the created payments carry. Typing a date more than a month back shows a "Payment date too early" warning, but you may continue. A date in the future is refused when you click "Clear" ("Payment date too late").',
      section: 'Clearance: amounts and saving',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Nustatyti mokėjimo datą',
          body: 'Data, kurią gaus sukurti mokėjimai. Įvedus datą, ankstesnę nei prieš mėnesį, rodomas įspėjimas "Payment date too early", bet galima tęsti. Ateities data atmetama paspaudus "Išvalyti" ("Payment date too late").',
        },
        lv: {
          title: 'Iestatīt maksājuma datumu',
          body: 'Datums, ar kuru tiks izveidoti maksājumi. Ievadot datumu, kas ir vairāk nekā mēnesi senāks, parādās brīdinājums "Payment date too early", taču var turpināt. Nākotnes datums tiek noraidīts, nospiežot "Clear" ("Payment date too late").',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-comment"]',
      title: 'Add a comment to the payments',
      body: 'Optional text put in front of "Cleared on: <date>" in the comment of every payment this clearance creates. The whole comment is cut off at 512 characters.',
      section: 'Clearance: amounts and saving',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Pridėti komentarą prie mokėjimų',
          body: 'Neprivalomas tekstas, įrašomas prieš "Cleared on: <data>" kiekvieno šio užskaitymo sukurto mokėjimo komentare. Visas komentaras nukerpamas ties 512 simbolių.',
        },
        lv: {
          title: 'Pievienot komentāru maksājumiem',
          body: 'Neobligāts teksts, kas tiek ierakstīts pirms "Cleared on: <datums>" katra šīs ieskaitīšanas izveidotā maksājuma komentārā. Viss komentārs tiek nogriezts pie 512 rakstzīmēm.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-entries-grid"]',
      title: 'Decide how much each entry gets',
      body: 'One row per outstanding premium entry with policy, reminder number, payer, insurer, booking date, premium, paid amounts and "Debt" (or "Discount debt" for GrECo\'s own balance). Only "To be cleared" can be changed: click the cell and type the amount. A row with 0 creates no payment.',
      section: 'Clearance: amounts and saving',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Nuspręsti, kiek skirti kiekvienai įmokai',
          body: 'Kiekviena eilutė yra neapmokėta įmoka su polisu, priminimo numeriu, mokėtoju, draudiku, užregistravimo data, įmoka, sumokėtomis sumomis ir "Skola" (arba "Neapmokėta nuolaida" GrECo balansui). Keisti galima tik "To be cleared": spustelėkite langelį ir įveskite sumą. Eilutė su 0 mokėjimo nesukuria.',
        },
        lv: {
          title: 'Noteikt, cik saņem katrs ieraksts',
          body: 'Katra rinda ir neapmaksāta prēmija ar polisi, atgādinājuma numuru, maksātāju, apdrošinātāju, grāmatošanas datumu, prēmiju, samaksātajām summām un "Debt" (vai "Discount debt" GrECo bilancei). Mainīt var tikai "To be cleared": noklikšķiniet uz šūnas un ievadiet summu. Rinda ar 0 maksājumu neizveido.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-fill-all"]',
      title: 'Spread the balance over all entries',
      body: 'The lightning button in the "To be cleared" header ("Select all") fills every row in list order with its debt until the balance is used up; the remaining rows get 0. Sort the list first if a different order should be paid first.',
      section: 'Clearance: amounts and saving',
      context: 'clear-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Paskirstyti balansą visoms įmokoms',
          body: 'Žaibo mygtukas "To be cleared" antraštėje ("Pasirinkti viską") užpildo kiekvieną eilutę jos skola sąrašo tvarka, kol balansas išnaudojamas; likusioms eilutėms lieka 0. Jei pirmiausia reikia apmokėti kita tvarka, iš pradžių surūšiuokite sąrašą.',
        },
        lv: {
          title: 'Sadalīt bilanci visiem ierakstiem',
          body: 'Zibens poga kolonnas "To be cleared" virsrakstā ("Select all") aizpilda katru rindu ar tās parādu saraksta secībā, līdz bilance izlietota; pārējās rindas saņem 0. Ja vispirms jāapmaksā citā secībā, vispirms sakārtojiet sarakstu.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-zero-all"]',
      title: 'Reset all amounts to zero',
      body: 'The X button in the "To be cleared" header ("Clear all") sets every row to 0, so you can start over and pick the entries one by one.',
      section: 'Clearance: amounts and saving',
      context: 'clear-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Nustatyti visas sumas į nulį',
          body: 'X mygtukas "To be cleared" antraštėje ("Clear all") visoms eilutėms nustato 0, kad galėtumėte pradėti iš naujo ir pasirinkti įmokas po vieną.',
        },
        lv: {
          title: 'Atiestatīt visas summas uz nulli',
          body: 'X poga kolonnas "To be cleared" virsrakstā ("Clear all") visām rindām iestata 0, lai varētu sākt no jauna un izvēlēties ierakstus pa vienam.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-row-fill"]',
      title: 'Fill one entry from the balance',
      body: 'The lightning button in a row puts what is left of the balance into that row, capped at its debt. Ctrl+click marks several rows and Shift+click marks a range; a plain click on a marked row then fills all marked rows from the full balance, ignoring amounts already on other rows, so check the total.',
      section: 'Clearance: amounts and saving',
      context: 'clear-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Užpildyti vieną įmoką iš balanso',
          body: 'Žaibo mygtukas eilutėje įrašo likusią balanso dalį, bet ne daugiau nei eilutės skola. Ctrl+spustelėjimas pažymi kelias eilutes, Shift+spustelėjimas pažymi intervalą; paprastas spustelėjimas ant pažymėtos eilutės užpildo visas pažymėtas eilutes iš viso balanso, neatsižvelgiant į kitose eilutėse jau esančias sumas, todėl patikrinkite bendrą sumą.',
        },
        lv: {
          title: 'Aizpildīt vienu ierakstu no bilances',
          body: 'Zibens poga rindā ieraksta atlikušo bilances daļu, bet ne vairāk par rindas parādu. Ctrl+klikšķis atzīmē vairākas rindas, Shift+klikšķis atzīmē intervālu; parasts klikšķis uz atzīmētas rindas tad aizpilda visas atzīmētās rindas no visas bilances, neņemot vērā citās rindās jau esošās summas, tāpēc pārbaudiet kopsummu.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-row-zero"]',
      title: 'Set one entry to zero',
      body: 'The X button in a row sets its "To be cleared" amount to 0. Ctrl+click and Shift+click mark several rows the same way as the lightning button; a plain click on a marked row then zeroes all marked rows.',
      section: 'Clearance: amounts and saving',
      context: 'clear-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Nustatyti vienos įmokos sumą į nulį',
          body: 'X mygtukas eilutėje nustato jos "To be cleared" sumą į 0. Ctrl+spustelėjimu ir Shift+spustelėjimu kelios eilutės pažymimos taip pat kaip žaibo mygtuku; paprastas spustelėjimas ant pažymėtos eilutės tada visoms pažymėtoms nustato 0.',
        },
        lv: {
          title: 'Iestatīt viena ieraksta summu uz nulli',
          body: 'X poga rindā iestata tās "To be cleared" summu uz 0. Ar Ctrl+klikšķi un Shift+klikšķi vairākas rindas atzīmē tāpat kā ar zibens pogu; parasts klikšķis uz atzīmētas rindas tad visām atzīmētajām iestata 0.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-highlight-search"]',
      title: 'Highlight matching entries',
      body: 'Type a policy, client, insurer, number, amount or date (dd.mm.yyyy) to highlight the rows that contain it; the number of matching rows appears next to the box. It does not hide any rows.',
      section: 'Clearance: amounts and saving',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Paryškinti atitinkančias įmokas',
          body: 'Įveskite polisą, klientą, draudiką, numerį, sumą arba datą (dd.mm.yyyy), kad būtų paryškintos ją turinčios eilutės; atitinkančių eilučių skaičius rodomas šalia laukelio. Eilutės nepaslepiamos.',
        },
        lv: {
          title: 'Izcelt atbilstošos ierakstus',
          body: 'Ievadiet polisi, klientu, apdrošinātāju, numuru, summu vai datumu (dd.mm.yyyy), lai izceltu rindas, kurās tas ir; atbilstošo rindu skaits parādās blakus laukam. Rindas netiek paslēptas.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-total"]',
      title: 'Compare the total with the balance',
      body: 'Shows "To be cleared: total / balance currency (rows)". The total turns red when it is higher than the balance, and "Clear" stays disabled until you lower it.',
      section: 'Clearance: amounts and saving',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Palyginti sumą su balansu',
          body: 'Rodoma "To be cleared: suma / balansas valiuta (eilutės)". Suma tampa raudona, kai viršija balansą, o "Išvalyti" lieka neaktyvus, kol jos nesumažinsite.',
        },
        lv: {
          title: 'Salīdzināt kopsummu ar bilanci',
          body: 'Rāda "To be cleared: kopsumma / bilance valūta (rindas)". Kopsumma kļūst sarkana, ja tā pārsniedz bilanci, un "Clear" paliek neaktīva, līdz to samazināsiet.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-submit"]',
      title: 'Save the clearance',
      body: '"Clear" creates a payment for every row with an amount above 0, paid from this balance, then closes the dialog and reloads the list. The server refuses the whole clearance if an amount is above the entry\'s debt, an entry is already paid, or an entry is outside the chosen clients, currency, insurers, payers, service segments or company.',
      section: 'Clearance: amounts and saving',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Išsaugoti užskaitymą',
          body: '"Išvalyti" sukuria mokėjimą kiekvienai eilutei, kurios suma didesnė nei 0, apmokant iš šio balanso, tada uždaro langą ir iš naujo įkelia sąrašą. Serveris atmeta visą užskaitymą, jei suma viršija įmokos skolą, įmoka jau apmokėta arba įmoka nepatenka į pasirinktus klientus, valiutą, draudikus, mokėtojus, paslaugų segmentus ar įmonę.',
        },
        lv: {
          title: 'Saglabāt ieskaitīšanu',
          body: '"Clear" izveido maksājumu katrai rindai ar summu virs 0, apmaksājot no šīs bilances, pēc tam aizver logu un pārlādē sarakstu. Serveris noraida visu ieskaitīšanu, ja summa pārsniedz ieraksta parādu, ieraksts jau apmaksāts vai ieraksts neatbilst izvēlētajiem klientiem, valūtai, apdrošinātājiem, maksātājiem, pakalpojumu segmentiem vai uzņēmumam.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-clear-close"]',
      title: 'Close without clearing',
      body: 'Closes the dialog without creating any payment. The filters and amounts are not kept; the next time the dialog opens with fresh suggestions.',
      section: 'Clearance: amounts and saving',
      context: 'clear-dialog',
      i18n: {
        lt: {
          title: 'Uždaryti neužskaičius',
          body: 'Uždaro langą nesukurdamas jokio mokėjimo. Filtrai ir sumos neišsaugomi; kitą kartą langas atsidarys su naujais pasiūlymais.',
        },
        lv: {
          title: 'Aizvērt bez ieskaitīšanas',
          body: 'Aizver logu, neizveidojot nevienu maksājumu. Filtri un summas netiek saglabāti; nākamreiz logs atvērsies ar jauniem ieteikumiem.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-info-dialog"]',
      title: 'Read the balance history',
      body: '"Client balance clearance information" shows one client\'s balance for one company and currency. It is read-only: corrections are made on the page where the money was booked, such as Bank statements.',
      section: 'Balance history',
      context: 'info-dialog',
      i18n: {
        lt: {
          title: 'Skaityti balanso istoriją',
          body: '"Client balance clearance information" rodo vieno kliento balansą vienoje įmonėje ir viena valiuta. Jis skirtas tik skaityti: pataisymai daromi tame puslapyje, kuriame pinigai buvo užregistruoti, pvz., "Banko išrašai".',
        },
        lv: {
          title: 'Lasīt bilances vēsturi',
          body: '"Client balance clearance information" rāda viena klienta bilanci vienā uzņēmumā un vienā valūtā. Tas ir tikai lasāms: labojumus veic lapā, kur nauda tika iegrāmatota, piemēram, "Bankas konta izraksti".',
        },
      },
    },
    {
      selector: '[data-help="client-balance-info-entries-grid"]',
      title: 'Follow the balance entries',
      body: '"Client balance entries" lists every entry, newest first, with its amount and the "Current balance" right after it. Further columns show when it was created, the payment date, whether it came from a bank statement entry clearance, the statement date, the payment reference number, the account numbers and the comment.',
      section: 'Balance history',
      context: 'info-dialog',
      i18n: {
        lt: {
          title: 'Peržiūrėti balanso įrašus',
          body: '"Kliento balanso įrašai" pateikia visus įrašus nuo naujausio su suma ir iškart po jo buvusiu "Current balance". Kituose stulpeliuose matyti sukūrimo laikas, mokėjimo data, ar įrašas atsirado užskaitant banko išrašo įrašą, išrašo data, mokėjimo numeris, sąskaitų numeriai ir komentaras.',
        },
        lv: {
          title: 'Sekot bilances ierakstiem',
          body: '"Klienta bilances ieraksti" uzskaita visus ierakstus, jaunākos vispirms, ar summu un "Current balance" uzreiz pēc tā. Pārējās kolonnās redzams izveides laiks, maksājuma datums, vai ieraksts radies, ieskaitot bankas izraksta ierakstu, izraksta datums, maksājuma atsauces numurs, kontu numuri un komentārs.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-info-entry-payments"]',
      title: 'See what a balance entry paid',
      body: 'Some negative entries carry an i button at the end of the row. Click it to list below which payments that entry settled and to highlight the row; click it again to hide the list.',
      section: 'Balance history',
      context: 'info-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Pažiūrėti, ką apmokėjo balanso įrašas',
          body: 'Kai kurių neigiamų įrašų eilutės gale yra mygtukas i. Spustelėkite jį, kad žemiau būtų parodyti šiuo įrašu padengti mokėjimai, o eilutė paryškinta; spustelėkite dar kartą, kad sąrašą paslėptumėte.',
        },
        lv: {
          title: 'Redzēt, ko apmaksāja bilances ieraksts',
          body: 'Dažiem negatīviem ierakstiem rindas beigās ir poga i. Noklikšķiniet uz tās, lai zemāk parādītu ar šo ierakstu nokārtotos maksājumus un izceltu rindu; noklikšķiniet vēlreiz, lai sarakstu paslēptu.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-info-payments-grid"]',
      title: 'Check the settled payments',
      body: '"Payments settled by this client balance entry" shows the premium payments funded by the selected entry, with their policy, insurer, amount and declaration date. "No data" means the entry did not settle any payment.',
      section: 'Balance history',
      context: 'info-payments',
      optional: true,
      i18n: {
        lt: {
          title: 'Patikrinti padengtus mokėjimus',
          body: '"Šiuo kliento balanso įrašu padengti mokėjimai" rodo pasirinkto įrašo apmokėtus įmokų mokėjimus su polisu, draudiku, suma ir deklaravimo data. "No data" reiškia, kad įrašas nepadengė jokio mokėjimo.',
        },
        lv: {
          title: 'Pārbaudīt nokārtotos maksājumus',
          body: '"Maksājumi, kas nokārtoti ar šo klienta bilances ierakstu" rāda izvēlētā ieraksta apmaksātos prēmiju maksājumus ar polisi, apdrošinātāju, summu un deklarēšanas datumu. "No data" nozīmē, ka ieraksts nav nokārtojis nevienu maksājumu.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-info-export"]',
      title: 'Export the balance entries to Excel',
      body: '"Export Excel" downloads the client balance entries as an Excel file. The button only appears when the balance has at least one entry.',
      section: 'Balance history',
      context: 'info-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Eksportuoti balanso įrašus į Excel',
          body: '"Export Excel" atsisiunčia kliento balanso įrašus Excel failu. Mygtukas rodomas tik tada, kai balansas turi bent vieną įrašą.',
        },
        lv: {
          title: 'Eksportēt bilances ierakstus uz Excel',
          body: '"Export Excel" lejupielādē klienta bilances ierakstus Excel failā. Poga parādās tikai tad, ja bilancei ir vismaz viens ieraksts.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-info-export-payments"]',
      title: 'Export the settled payments to Excel',
      body: '"Export payments to Excel" downloads the payments settled by the selected balance entry. It appears only while that list is open and has rows.',
      section: 'Balance history',
      context: 'info-payments',
      optional: true,
      i18n: {
        lt: {
          title: 'Eksportuoti padengtus mokėjimus į Excel',
          body: '"Eksportuoti mokėjimus į Excel" atsisiunčia pasirinkto balanso įrašo padengtus mokėjimus. Mygtukas rodomas tik tada, kai šis sąrašas atvertas ir jame yra eilučių.',
        },
        lv: {
          title: 'Eksportēt nokārtotos maksājumus uz Excel',
          body: '"Eksportēt maksājumus uz Excel" lejupielādē izvēlētā bilances ieraksta nokārtotos maksājumus. Poga redzama tikai tad, kad šis saraksts ir atvērts un tajā ir rindas.',
        },
      },
    },
    {
      selector: '[data-help="client-balance-info-close"]',
      title: 'Close the balance history',
      body: 'Closes "Client balance clearance information" and returns to the client balance list. Nothing was changed while it was open.',
      section: 'Balance history',
      context: 'info-dialog',
      i18n: {
        lt: {
          title: 'Uždaryti balanso istoriją',
          body: 'Uždaro "Client balance clearance information" ir grąžina į klientų balansų sąrašą. Kol langas buvo atvertas, niekas nepakeista.',
        },
        lv: {
          title: 'Aizvērt bilances vēsturi',
          body: 'Aizver "Client balance clearance information" un atgriežas klientu bilanču sarakstā. Kamēr logs bija atvērts, nekas netika mainīts.',
        },
      },
    },
  ],
  i18n: {
    lt: {
      title: 'Kliento balansas',
      intro:
        'Visi klientų balansai (saldo) pagal GrECo įmonę ir valiutą: iš kliento gauti pinigai, dar nesusieti su jokia įmoka, dažniausiai permokėta užskaityto banko išrašo įrašo dalis. Čia šį balansą užskaitote kliento neapmokėtoms įmokoms ir taip sukuriate mokėjimus arba peržiūrite, kaip balansas susidarė ir buvo panaudotas.',
      tips: [
        'Klientas turi po vieną eilutę kiekvienai GrECo įmonei ir valiutai. Teigiamas balansas yra pinigai, kuriuos dar galima panaudoti įmokoms; čia juos užskaičius sukuriami tikri mokėjimai, kurie vėliau matomi puslapyje "Mokėjimai" deklaravimui draudikui.',
        'Jei lango pavadinimas baigiasi "(discounts)", balansas priklauso pačiam GrECo: užskaitymas tada dengia neapmokėtas nuolaidas vietoje įmokų skolos, prasideda be pasirinkto kliento ir siūlo filtrą "Broker premium collection".',
        'Sąrašas pats neatsinaujina. Užskaitę banko išrašus arba kai kolega dirbo su tuo pačiu klientu, spustelėkite atnaujinimo mygtuką.',
        'Matote tik įmonių meniu pažymėtų GrECo įmonių balansus ir tik tuos klientus, kuriems turite teises. Klientai už jūsų teisių grupių ribų praleidžiami be jokio pranešimo.',
      ],
      sections: {
        'Getting around': 'Orientacija',
        'Finding a client balance': 'Kliento balanso paieška',
        'Reading the list': 'Sąrašo skaitymas',
        'Clearance: choosing the entries': 'Užskaitymas: įmokų pasirinkimas',
        'Clearance: amounts and saving': 'Užskaitymas: sumos ir išsaugojimas',
        'Balance history': 'Balanso istorija',
      },
      contexts: {
        'clear-dialog': 'Spustelėkite mygtuką "Užskaityti kliento balansą" (išsišakojusios rodyklės piktograma) kliento eilutės gale.',
        'info-dialog': 'Spustelėkite mygtuką "Kliento balanso informacija" (piktograma i) kliento eilutės gale.',
        'info-payments':
          'Lange "Client balance clearance information" spustelėkite mygtuką i balanso įrašo, kuris jį turi, eilutės gale.',
      },
    },
    lv: {
      title: 'Klienta bilance',
      intro:
        'Visas klientu bilances (saldo) pa GrECo uzņēmumiem un valūtām: no klienta saņemtā nauda, kas vēl nav sasaistīta ar nevienu prēmiju, visbiežāk ieskaitīta bankas izraksta ieraksta pārmaksātā daļa. Šeit šo bilanci ieskaita klienta neapmaksātajās prēmijās, izveidojot maksājumus, vai apskata, kā bilance veidojusies un izlietota.',
      tips: [
        'Klientam ir viena rinda katram GrECo uzņēmumam un valūtai. Pozitīva bilance ir nauda, ko vēl var izmantot prēmijām; ieskaitot to šeit, tiek izveidoti īsti maksājumi, kas pēc tam redzami lapā "Payments" deklarēšanai apdrošinātājam.',
        'Ja loga virsraksts beidzas ar "(discounts)", bilance pieder pašam GrECo: ieskaitīšana tad sedz atlaižu parādu, nevis prēmiju parādu, sākas bez izvēlēta klienta un piedāvā filtru "Broker premium collection".',
        'Saraksts pats neatjaunojas. Pēc bankas izrakstu ieskaitīšanas vai kad kolēģis strādājis ar to pašu klientu, noklikšķiniet uz atjaunošanas pogas.',
        'Redzat tikai uzņēmumu izvēlnē atzīmēto GrECo uzņēmumu bilances un tikai tos klientus, kuriem jums ir tiesības. Klienti ārpus jūsu tiesību grupām tiek izlaisti bez jebkāda paziņojuma.',
      ],
      sections: {
        'Getting around': 'Orientēšanās',
        'Finding a client balance': 'Klienta bilances meklēšana',
        'Reading the list': 'Saraksta lasīšana',
        'Clearance: choosing the entries': 'Ieskaitīšana: ierakstu izvēle',
        'Clearance: amounts and saving': 'Ieskaitīšana: summas un saglabāšana',
        'Balance history': 'Bilances vēsture',
      },
      contexts: {
        'clear-dialog': 'Noklikšķiniet uz pogas "Clear client balance" (sazarotas bultas ikona) klienta rindas beigās.',
        'info-dialog': 'Noklikšķiniet uz pogas "Client balance information" (ikona i) klienta rindas beigās.',
        'info-payments':
          'Logā "Client balance clearance information" noklikšķiniet uz pogas i tā bilances ieraksta rindas beigās, kuram tā ir.',
      },
    },
  },
};
